import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  OverlayDrawer,
  Select,
  Spinner,
  Textarea
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { MessageBarType } from '@fluentui/react';
import { useTranslation } from 'react-i18next';

import { NewTenantPriceList } from '../../../../models/Entities/TenantPriceLists/NewTenantPriceList';
import { TenantPriceList } from '../../../../models/Entities/TenantPriceLists/TenantPriceList';
import { MessageComponent } from '../../../../commons/component/MessageComponent';
import * as ComponentFunctions from './TenantPriceListFormFunctions';
import { useStyles } from './TenantPriceListForm.Styles';

export const TenantPriceListFormComponent = (props: ComponentFunctions.Props) => {
  const styles = useStyles();
  const [t] = useTranslation();

  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [formValidation, setFormValidation] = useState<
    ComponentFunctions.FormValidation | undefined
  >();
  const [formState, setFormState] = useState<ComponentFunctions.TenantPriceListFormState>(
    ComponentFunctions.initialFormState
  );

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (props.TenantPriceList) {
      setFormState(ComponentFunctions.formStateFromTenantPriceList(props.TenantPriceList));
    } else {
      ResetForm();
    }
  }, [props.TenantPriceList]);

  useEffect(() => {
    if (submittedForm && (props.addingSuccess || props.savingSuccess)) {
      ResetForm();
      props.dismissPanel();
    }
  }, [props.addingSuccess, props.savingSuccess, submittedForm]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const ResetForm = () => {
    setFormState(ComponentFunctions.initialFormState);
    setFormValidation(undefined);
    setSubmittedForm(false);
  };

  const patch = (partial: Partial<ComponentFunctions.TenantPriceListFormState>) =>
    setFormState((s) => ({ ...s, ...partial }));

  // ── Events ─────────────────────────────────────────────────────────────────

  const HandleCancelForm = () => {
    ResetForm();
    props.dismissPanel();
  };

  const HandleSaveForm = () => {
    setSubmittedForm(true);
    const validation = ComponentFunctions.validateForm(formState, t);
    setFormValidation(validation);

    if (validation.success) {
      if (props.TenantPriceList) {
        const updated = {
          ...props.TenantPriceList,
          listName: formState.listName!,
          comments: formState.comments || '',
          priceListType: formState.priceListType!
        };
        props.UpdateTenantPriceList(updated as TenantPriceList);
      } else {
        const newItem: NewTenantPriceList = {
          listName: formState.listName!,
          comments: formState.comments || '',
          priceListType: formState.priceListType!
        };
        props.AddTenantPriceList(newItem);
      }
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const isSaving = (props.isAdding || props.isSaving) ?? false;

  return (
    <OverlayDrawer position="end" open={props.isOpen}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={t('tenantPriceListForm.modal.closeButton')}
              icon={<Dismiss24Regular />}
              onClick={HandleCancelForm}
            />
          }
        >
          {t('tenantPriceListForm.modal.priceList')}
        </DrawerHeaderTitle>

        {submittedForm && formValidation?.success === false && (
          <MessageComponent
            message={t('tenantPriceListForm.modal.reviewInformation')}
            type={MessageBarType.error}
            itemList={formValidation.errors}
          />
        )}

        {submittedForm &&
          (props.failOnSaving || props.failOnAdding) &&
          props.error && (
            <MessageComponent
              message={t('tenantPriceListForm.modal.errorMessage')}
              subMessage={props.error.ErrorMessage}
              itemList={props.error.Errors}
              type={MessageBarType.error}
            />
          )}
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <Field
          label={t('tenantPriceListForm.formFields.name')}
          required
          validationMessage={
            submittedForm && !formState.listName?.trim()
              ? t('tenantPriceListForm.formFields.requiredField')
              : undefined
          }
        >
          <Input
            value={formState.listName ?? ''}
            onChange={(_, data) => patch({ listName: data.value || undefined })}
            autoComplete="off"
          />
        </Field>

        <Field
          label={t('tenantPriceListForm.formFields.type')}
          required
          validationMessage={
            submittedForm && !formState.priceListType
              ? t('tenantPriceListForm.formFields.requiredField')
              : undefined
          }
        >
          <Select
            aria-label={t('tenantPriceListForm.formFields.type')}
            title={t('tenantPriceListForm.formFields.type')}
            value={formState.priceListType ?? ''}
            onChange={(_, data) => patch({ priceListType: data.value || undefined })}
          >
            <option value="">{t('tenantPriceListForm.formFields.select')}</option>
            {ComponentFunctions.PriceListTypeOptions.map((opt) => (
              <option key={opt.key as string} value={opt.key as string}>
                {opt.text}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('tenantPriceListForm.formFields.comments')}>
          <Textarea
            value={formState.comments ?? ''}
            onChange={(_, data) => patch({ comments: data.value || undefined })}
          />
        </Field>
      </DrawerBody>

      <DrawerFooter className={styles.footer}>
        <Button onClick={HandleCancelForm} disabled={isSaving}>
          {t('tenantPriceListForm.footer.cancelButton')}
        </Button>
        <Button appearance="primary" onClick={HandleSaveForm} disabled={isSaving}>
          {isSaving && (
            <>
              <Spinner size="extra-tiny" />
              &nbsp;
            </>
          )}
          {t('tenantPriceListForm.footer.saveButton')}
        </Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(TenantPriceListFormComponent as any);

